import { createContext, ReactNode, useContext, useMemo } from 'react';

/**
 * The document the assessment UI is currently living in.
 *
 * Everything below `InAppAssessmentWindow` reads its document, window, and portal container from
 * here instead of touching the globals, so the same panel can be rendered into a
 * Document Picture-in-Picture window. Only the window shell knows which document it is.
 */
export interface AssessmentWindowContextValue {
  ownerDocument: Document;
  ownerWindow: Window;
  /** Where Radix portals (dropdowns, popovers) should mount so they land in the same document. */
  portalContainer: HTMLElement | null;
}

const AssessmentWindowContext = createContext<AssessmentWindowContextValue | null>(null);

export function AssessmentWindowProvider({
  ownerDocument,
  ownerWindow,
  portalContainer,
  children,
}: AssessmentWindowContextValue & { children: ReactNode }) {
  const value = useMemo(
    () => ({ ownerDocument, ownerWindow, portalContainer }),
    [ownerDocument, ownerWindow, portalContainer],
  );

  return <AssessmentWindowContext.Provider value={value}>{children}</AssessmentWindowContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAssessmentWindow(): AssessmentWindowContextValue {
  const context = useContext(AssessmentWindowContext);
  if (!context) {
    throw new Error('useAssessmentWindow must be used inside an InAppAssessmentWindow');
  }
  return context;
}
